import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  useDeleteUserStateItemMutation,
  useUpdateUserStateItemMutation,
} from '../redux/services/userStateApi';
import type { UserStateEntry } from '../types/user-state';
import { useAuth } from './useAuth';
import { setEntry, removeEntry } from '../redux/features/userState/userStateSlice';
import { buildSavedCompanyItemKey } from '../utils/user-state/saved-company-item-key';
import { normalizeUserStateVersion } from '../utils/user-state/normalize-user-state-version';
import type { RelationshipTag } from '../utils/job-tracking/relationship-tag';
import type { WatchlistSeenSnapshot } from '../utils/user-state/watchlist-seen-snapshot';

/**
 * Follow/unfollow a company for the Watchlist (SEEKER_HUB_PLAN.md Phase 4): a followed
 * company is a live-data subscription (open roles, employer score, trend), not a bookmark —
 * but its storage mechanics mirror useSavedJobActions.ts exactly (guest-first Redux/localforage,
 * synced to UserState when authenticated). companyId PKs are case-sensitive — never lowercase.
 */
export function useCompanyFollowActions() {
  const dispatch = useAppDispatch();
  const userStateEntries = useAppSelector((state) => state.userState?.entries ?? {});
  const [updateUserStateItem] = useUpdateUserStateItemMutation();
  const [deleteUserStateItem] = useDeleteUserStateItemMutation();
  const { isAuthenticated } = useAuth();

  const followCompany = useCallback(
    async (companyId: string, extras: { companyName?: string } = {}): Promise<boolean> => {
      const itemKey = buildSavedCompanyItemKey(companyId);
      const followedAt = new Date().toISOString();
      const data = { companyId, companyName: extras.companyName ?? null, followedAt };

      // Guest-first: always land locally so the UI reflects the follow immediately.
      dispatch(setEntry({ itemKey, entry: { data, version: 0, updatedAt: followedAt } }));

      if (!isAuthenticated) return true;

      const currentEntry = userStateEntries[itemKey] as UserStateEntry | undefined;
      const version = normalizeUserStateVersion(currentEntry?.version);
      try {
        const result = await updateUserStateItem({ itemKey, data, version }).unwrap();
        if (result) dispatch(setEntry({ itemKey, entry: result }));
        return true;
      } catch (error) {
        const conflictEntry = extractConflictEntry(error);
        if (conflictEntry) {
          dispatch(setEntry({ itemKey, entry: conflictEntry }));
          return true;
        }
        dispatch(removeEntry(itemKey));
        return false;
      }
    },
    [dispatch, isAuthenticated, updateUserStateItem, userStateEntries]
  );

  const unfollowCompany = useCallback(
    async (companyId: string): Promise<boolean> => {
      const itemKey = buildSavedCompanyItemKey(companyId);
      const previous = userStateEntries[itemKey] as UserStateEntry | undefined;

      dispatch(removeEntry(itemKey));

      if (!isAuthenticated) return true;

      try {
        await deleteUserStateItem({ itemKey, version: previous?.version }).unwrap();
        return true;
      } catch (error) {
        const conflictEntry = extractConflictEntry(error);
        if (previous) dispatch(setEntry({ itemKey, entry: conflictEntry ?? previous }));
        return false;
      }
    },
    [dispatch, deleteUserStateItem, isAuthenticated, userStateEntries]
  );

  /** Patches notes/relationshipTag/watchlistSeen onto an existing followed-company entry, preserving companyName/followedAt. */
  const updateCompanyMeta = useCallback(
    async (
      companyId: string,
      patch: { notes?: string; relationshipTag?: RelationshipTag | null; watchlistSeen?: WatchlistSeenSnapshot }
    ): Promise<boolean> => {
      const itemKey = buildSavedCompanyItemKey(companyId);
      const currentEntry = userStateEntries[itemKey] as UserStateEntry | undefined;
      const currentData = (currentEntry?.data as Record<string, unknown> | undefined) ?? { companyId };
      const data = {
        ...currentData,
        companyId,
        ...(patch.notes !== undefined ? { notes: patch.notes } : {}),
        ...(patch.relationshipTag !== undefined ? { relationshipTag: patch.relationshipTag } : {}),
        ...(patch.watchlistSeen !== undefined ? { watchlistSeen: patch.watchlistSeen } : {}),
      };

      dispatch(setEntry({ itemKey, entry: { data, version: currentEntry?.version ?? 0, updatedAt: currentEntry?.updatedAt ?? null } }));

      if (!isAuthenticated) return true;

      const version = normalizeUserStateVersion(currentEntry?.version);
      try {
        const result = await updateUserStateItem({ itemKey, data, version }).unwrap();
        if (result) dispatch(setEntry({ itemKey, entry: result }));
        return true;
      } catch (error) {
        const conflictEntry = extractConflictEntry(error);
        if (conflictEntry) {
          // Keep our just-typed edit locally even if the server had a newer version of the
          // rest of the entry — merge our patch on top rather than discarding it.
          const mergedData = { ...(conflictEntry.data as Record<string, unknown>), ...data };
          dispatch(setEntry({ itemKey, entry: { ...conflictEntry, data: mergedData } }));
          return true;
        }
        return false;
      }
    },
    [dispatch, isAuthenticated, updateUserStateItem, userStateEntries]
  );

  return { followCompany, unfollowCompany, updateCompanyMeta };
}

function extractConflictEntry(error: unknown): UserStateEntry | null {
  if (!error || typeof error !== 'object') return null;
  const maybeError = error as { status?: number; data?: unknown; error?: unknown };
  if ('data' in maybeError && 'status' in maybeError && maybeError.status === 409) {
    return (maybeError.data as UserStateEntry) ?? null;
  }
  if ('error' in maybeError && maybeError.error && typeof maybeError.error === 'object') {
    const inner = maybeError.error as { status?: number; data?: unknown };
    if (inner.status === 409) return inner.data as UserStateEntry;
  }
  return null;
}
